/**
 * Content Authoring Portal enums — shared wire/API values for templates and related entities.
 *
 * Keep authoring domain constants here so UI, API adapters, and tests share one source of truth.
 */

/** Template composition type (`authoring_templates.template_type`). */
export const TEMPLATE_TYPES = {
  STANDALONE: 'standalone',
  COC_MODULE: 'coc_module',
  COC_TRAINING: 'coc_training',
} as const;

export type TemplateType = (typeof TEMPLATE_TYPES)[keyof typeof TEMPLATE_TYPES];

/**
 * The types `POST /templates` can GENERATE from a storyboard. A `coc_training` is never
 * generated — it is composed from modules that already exist, through a different request
 * shape (`module_ids`) and a different client function (CEE-734).
 */
export type GeneratedTemplateType = Exclude<
  TemplateType,
  typeof TEMPLATE_TYPES.COC_TRAINING
>;

/**
 * Template lifecycle status (`authoring_templates.status`, draft → ready).
 *
 * Neither `published` nor `archived` is a status: publishing is a course concept, and
 * archived is `deleted_at IS NOT NULL` — filtered by the API's `archived` param and
 * reported as a separate boolean field on every row (CEE-1066). A row therefore answers
 * two independent questions, and the label the grid draws is
 * `archived ? 'Archived' : <status>`, computed in one place per screen rather than being
 * a value in this union.
 */
export const TEMPLATE_STATUSES = {
  DRAFT: 'draft',
  READY: 'ready',
} as const;

export type TemplateStatus = (typeof TEMPLATE_STATUSES)[keyof typeof TEMPLATE_STATUSES];

/**
 * Authoring course lifecycle (`authoring_courses.status`).
 * `submitted` is used in the UI for in-review courses; the list API may return it
 * before the authoring-courses Lambda allowlist is extended.
 * `approved` is returned by backend after approval workflow.
 */
export const COURSE_STATUSES = {
  DRAFT: 'draft',
  READY: 'ready',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  PUBLISHED: 'published',
} as const;

export type CourseStatus = (typeof COURSE_STATUSES)[keyof typeof COURSE_STATUSES];

/**
 * Statuses surfaced on the Courses list grid and its Status filter (CEE-1094).
 * `archived` is a separate row flag, not a lifecycle status — offered in the filter
 * via `COURSE_STATUS_FILTER_ARCHIVED`. Legacy `approved` / `published` values may still
 * arrive from older rows but are not filter options.
 */
export const COURSES_LIST_STATUSES = [
  COURSE_STATUSES.DRAFT,
  COURSE_STATUSES.READY,
  COURSE_STATUSES.SUBMITTED,
] as const;

export type CoursesListStatus = (typeof COURSES_LIST_STATUSES)[number];
